import { ErrorBoundary } from '@eventbrite/error-boundary';
import React, { useContext } from 'react';
import { Divider } from '../_shared/Divider/Divider';
import { LayoutContext } from './Layout';
import styles from './Layout.module.scss';

export enum Modules {
    mainInfo = 'mainInfo',
    overview = 'overview',
    agenda = 'agenda',
    aside = 'aside',
    banners = 'banners',
    creatorCollections = 'creatorCollections',
    dateAndTime = 'dateAndTime',
    enhancedExpiredEventsBadge = 'enhancedExpiredEventsBadge',
    enhancedExpiredEvents = 'enhancedExpiredEvents',
    eventHero = 'eventHero',
    faq = 'faq',
    location = 'location',
    mainContent = 'mainContent',
    musicEventInfo = 'musicEventInfo',
    notifications = 'notifications',
    organizerBrief = 'organizerBrief',
    organizerPanel = 'organizerPanel',
    performers = 'performers',
    salesEndedMessage = 'salesEndedMessage',
    startDateAndLocation = 'startDateAndLocation',
    summary = 'summary',
    tags = 'tags',
    tickets = 'tickets',
    title = 'title',
    urgencySignals = 'urgencySignals',
    goodToKnow = 'goodToKnow',
    artistWidget = 'artistWidget',
    reportEvent = 'reportEvent',
    relatedEvents = 'relatedEvents',
    conversionBar = 'conversionBar',
    divider = 'divider',
    extraInfoAndActionBar = 'extraInfoAndActionBar',
}

const hiddenModules = {
    exploreMoreEvents: [
        Modules.location,
        Modules.dateAndTime,
        Modules.performers,
        Modules.agenda,
        Modules.overview,
        Modules.faq,
        Modules.banners,
        Modules.tags,
        Modules.organizerPanel,
        Modules.goodToKnow,
        Modules.reportEvent,
        Modules.relatedEvents,
        Modules.artistWidget,
        Modules.divider,
    ],
};

const Fallback = () => <div data-testid="fallback"></div>;

const StructuralModules = [Modules.aside, Modules.mainContent];

export const LayoutModule: React.FC<{
    name: Modules;
    testId?: string;
    noDivider?: boolean;
}> = ({ children, name, testId = name, noDivider = false }) => {
    const layout = useContext(LayoutContext);

    if (
        layout?.exploreMoreEvents &&
        hiddenModules.exploreMoreEvents.includes(name)
    ) {
        return null;
    }

    const classNames = [
        styles.module,
        ...(!StructuralModules.includes(name) ? [styles.section] : []),
        ...(styles[name] ? [styles[name]] : []),
    ];

    return (
        <ErrorBoundary fallback={<Fallback />}>
            <div
                className={classNames
                    .map((className) => className || 'undefined')
                    .join(' ')}
                data-testid={testId}
            >
                {children}
            </div>
            {!noDivider && <Divider id={name} className={styles.divider} />}
        </ErrorBoundary>
    );
};

export const MainContentModule: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    return (
        <LayoutModule name={Modules.mainContent} noDivider>
            {children}
        </LayoutModule>
    );
};

export const AsideModule: React.FC<{
    children: React.ReactNode;
    testId?: string;
}> = ({ children, testId }) => {
    return (
        <LayoutModule
            name={Modules.aside}
            noDivider
            testId={testId || Modules.aside}
        >
            {children}
        </LayoutModule>
    );
};
